import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, X, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

/**
 * Shared PayPal QR payment modal — used from Pricing, WhiteLabel,
 * 1040 & 1120 landing pages, and the AFSP Bundle page.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   plan: { id, name, amount }
 *   paypalHandle: string           // e.g. "Dynamictax80"
 *   user?: { email }               // optional — used to prefill the note step
 *   redirectAfter?: string         // path to send user after "I've paid"
 *   requireAuth?: boolean          // if true and no user, block submit (Pricing already handles this)
 */
export default function PayPalModal({
  open,
  onClose,
  plan,
  paypalHandle,
  user,
  redirectAfter,
  onSubmitted,
  onCaptured,
}) {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [paypalCfg, setPaypalCfg] = useState(null); // {client_id, mode, enabled}
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/paypal/config").then((r) => setPaypalCfg(r.data)).catch(() => setPaypalCfg({ enabled: false }));
    }
  }, [open]);

  if (!open || !plan) return null;

  const handle = (paypalHandle || "Dynamictax80").replace(/^@/, "");
  const payLink = `https://paypal.me/${handle}/${plan.amount}`;
  const automatedReady = paypalCfg?.enabled && paypalCfg?.client_id;

  const copyHandle = async () => {
    try {
      await navigator.clipboard.writeText(`@${handle}`);
      toast.success("PayPal handle copied.");
    } catch {
      toast.error("Couldn't copy — long-press to select the handle.");
    }
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/payments/paypal-request", {
        plan: plan.id,
        cashtag_note: note,
        method: "paypal",
      });
      toast.success("Payment logged. An instructor will verify and unlock your access.");
      setNote("");
      onSubmitted && onSubmitted();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="paypal-modal"
    >
      <div
        className="bg-card border border-border rounded-lg max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
          onClick={onClose}
          aria-label="Close"
          data-testid="paypal-modal-close"
        >
          <X size={18} />
        </button>
        <div className="label-caps mb-2 text-primary">PayPal · {plan.name}</div>
        <h3 className="font-serif text-3xl mb-4">
          Pay ${plan.amount.toFixed(0)} to @{handle}
        </h3>

        {/* Automated PayPal Checkout Buttons (only if server has credentials configured) */}
        {automatedReady && (
          <div className="mb-5" data-testid="paypal-checkout-buttons">
            <div className="text-center text-xs text-emerald-600 dark:text-emerald-400 mb-2 flex items-center justify-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Instant access · secure checkout {paypalCfg.mode === "sandbox" ? "(sandbox)" : ""}
            </div>
            <PayPalScriptProvider
              options={{
                clientId: paypalCfg.client_id,
                currency: "USD",
                intent: "capture",
              }}
            >
              <PayPalButtons
                style={{ layout: "vertical", label: "pay", shape: "rect" }}
                disabled={processing}
                createOrder={async () => {
                  try {
                    const r = await api.post("/paypal/create-order", { plan: plan.id });
                    return r.data.order_id;
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not start PayPal checkout");
                    throw e;
                  }
                }}
                onApprove={async (data) => {
                  setProcessing(true);
                  try {
                    const r = await api.post("/paypal/capture-order", { order_id: data.orderID });
                    if (r.data.captured) {
                      toast.success("Payment complete — access unlocked.");
                      onCaptured && onCaptured(r.data);
                      onClose();
                      // Full reload so /auth/me is refreshed everywhere.
                      setTimeout(() => window.location.reload(), 400);
                    } else {
                      toast.error("PayPal returned a non-completed status. Please try the QR fallback below.");
                    }
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Payment capture failed");
                  } finally {
                    setProcessing(false);
                  }
                }}
                onError={(err) => {
                  console.error("PayPal error:", err);
                  toast.error("PayPal ran into an issue — try the QR fallback below.");
                }}
              />
            </PayPalScriptProvider>
            {processing && (
              <div className="mt-2 flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <Loader2 size={12} className="animate-spin" /> Finalizing payment…
              </div>
            )}
            <div className="my-4 flex items-center gap-3">
              <div className="h-px bg-border flex-1" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">or pay by QR</span>
              <div className="h-px bg-border flex-1" />
            </div>
          </div>
        )}

        <div className="flex items-center justify-center mb-5" data-testid="paypal-qr">
          <div className="bg-white p-3 rounded-md border border-border">
            <img
              src="/paypal-qr.png"
              alt="PayPal QR code"
              className="h-[168px] w-[168px] object-contain"
            />
          </div>
        </div>
        <div className="text-center text-xs text-muted-foreground mb-5">
          📱 <strong>On your phone?</strong> Scan the QR — it opens PayPal with{" "}
          <span className="text-foreground">@{handle}</span> pre-filled.
        </div>
        <ol className="space-y-2 text-sm mb-5">
          <li>
            <strong>1.</strong> Send <strong>${plan.amount.toFixed(0)}</strong> to{" "}
            <strong>@{handle}</strong> via PayPal.
          </li>
          <li>
            <strong>2.</strong> Include your email
            {user?.email ? (
              <> (<em>{user.email}</em>)</>
            ) : (
              <> (the one you'll register with)</>
            )}{" "}
            in the PayPal note so we can match the payment.
          </li>
          <li>
            <strong>3.</strong> Tap the button below — an instructor will verify and unlock your access.
          </li>
        </ol>
        <div className="flex flex-wrap gap-2 mb-4">
          <Button variant="outline" size="sm" onClick={copyHandle} data-testid="paypal-copy-handle">
            <Copy size={14} className="mr-1.5" /> Copy @{handle}
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="paypal-external-link">
            <a href={payLink} target="_blank" rel="noreferrer">
              Open PayPal <ArrowRight size={14} className="ml-1.5" />
            </a>
          </Button>
        </div>
        <Input
          placeholder="Optional note (e.g., 'Sent at 3:12 PM from @yourhandle')"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="mb-3"
          data-testid="paypal-note-input"
        />
        {redirectAfter ? (
          <Button asChild className="w-full" data-testid="paypal-onboard-link">
            <Link to={user ? redirectAfter : `/register?next=${encodeURIComponent(redirectAfter)}`}>
              I've paid — continue <ArrowRight size={14} className="ml-2" />
            </Link>
          </Button>
        ) : (
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={onClose} data-testid="paypal-cancel-btn">Cancel</Button>
            <Button onClick={submit} disabled={sending} data-testid="paypal-submit-btn">
              {sending ? "Submitting…" : "I've paid — verify me"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
